import { v4 as uuidv4 } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import Module from '@/store/resource/common/module';
import CustomAgents from '@/store/resource/customAgents';
import api from '@/factories/api';

const TOOL_META = {
  message_sms: { group: 'Messaging', icon: 'sms' },
  send_email: { group: 'Messaging', icon: 'email' },
  notify_stakeholders: { group: 'Messaging', icon: 'notifications_active' },
  send_form: { group: 'Contact Data', icon: 'assignment' },
  update_contact_field: { group: 'Contact Data', icon: 'edit_note' },
  lookup_contacts: { group: 'Contact Data', icon: 'manage_search' },
  add_to_campaign: { group: 'Campaigns', icon: 'campaign' },
  create_task: { group: 'Tasks', icon: 'task_alt' },
  add_to_calendar: { group: 'Scheduling', icon: 'event' },
  search_portal: { group: 'Intelligence', icon: 'travel_explore' },
  mcp: { group: 'MCP', icon: 'extension' },
};

function createEmptyDraft() {
  return {
    name: '',
    icon: 'smart_toy',
    description: '',
    instructions: '',
    goal: '',
    stop_conditions: '',
    status: 'active',
    tools: [],
    teams: [],
    model: '',
  };
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

// Test-console calls get an explicit timeout: the app's axios factory sets
// none (axios default = wait forever), so one request stalled by a dying
// connection would wedge the console's busy flags permanently (C1b fix).
// All five calls are fast server-side (evals are dispatched to a queue, not
// awaited), so 20s is generous.
const CONSOLE_API_TIMEOUT_MS = 20000;

function normalizeTool(tool, configSchema = {}) {
  const meta = TOOL_META[tool.name] || {};
  return {
    tool_uuid: tool.tool_uuid,
    name: tool.name,
    label: tool.label || tool.name,
    summary: tool.summary || '',
    description: tool.description || '',
    allows_multiple_instances: Boolean(tool.allows_multiple_instances),
    group: meta.group || 'General',
    icon: meta.icon || 'build',
    configSchema,
  };
}

function normalizeToolInstance(tool) {
  return {
    instance_uuid: tool.instance_uuid || uuidv4(),
    tool_uuid: tool.tool_uuid,
    config: tool.config || {},
  };
}

function normalizeAgentDraft(agent) {
  if (!agent) return createEmptyDraft();
  return {
    ...agent,
    tools: toArray(agent.tools).map(normalizeToolInstance),
  };
}

function normalizeAvailableTools(catalog = [], schemas = {}) {
  return toArray(catalog)
    .filter((tool) => !tool.system)
    .map((tool) => normalizeTool(tool, schemas[tool.name]?.configSchema));
}

function normalizePayload(payload = {}) {
  const normalized = { ...payload };
  if (
    Object.prototype.hasOwnProperty.call(normalized, 'instructions') &&
    typeof normalized.instructions === 'string' &&
    normalized.instructions.trim() === ''
  ) {
    delete normalized.instructions;
  }
  return normalized;
}

let versionsFetchToken = 0;

const module = new Module(new CustomAgents());

module.add('state', {
  draft: null,
  availableTools: [],
  versions: [],
  versionsLoaded: false,
  publishedVersion: null,
  publishedVersionSnapshot: null,
  publishBaseline: null,
  compileResult: null,
  compiling: false,
  publishing: false,
  reviewAutosaving: false,
  reviewPreviewDirty: false,
  testContactId: '',
  testVersionSource: 'head',
});

module.add('mutations', {
  setDraft(state, agent) {
    const previousId = state.draft?.id;
    const nextId = agent?.id;
    if (previousId && previousId !== nextId) {
      state.testContactId = '';
      state.testVersionSource = 'head';
    }
    state.draft = normalizeAgentDraft(agent);
  },
  setDraftField(state, { key, value }) {
    state.draft = { ...(state.draft || createEmptyDraft()), [key]: value };
  },
  setAvailableTools(state, tools) {
    state.availableTools = tools;
  },
  setVersions(state, versions) {
    state.versions = versions;
    state.publishedVersion = versions.find((v) => v.is_active) || null;
    state.versionsLoaded = true;
  },
  resetVersionsLoaded(state) {
    state.versionsLoaded = false;
  },
  setPublishedVersionSnapshot(state, snapshot) {
    state.publishedVersionSnapshot = snapshot;
  },
  setPublishBaseline(state, baseline) {
    state.publishBaseline = baseline ? cloneDeep(baseline) : null;
  },
  setCompiling(state, val) {
    state.compiling = val;
  },
  setPublishing(state, val) {
    state.publishing = val;
  },
  setReviewAutosaving(state, val) {
    state.reviewAutosaving = val;
  },
  setReviewPreviewDirty(state, val) {
    state.reviewPreviewDirty = val;
  },
  setCompileResult(state, result) {
    state.compileResult = result;
  },
  resetReviewState(state) {
    state.compileResult = null;
    state.compiling = false;
    state.publishing = false;
    state.reviewAutosaving = false;
    state.reviewPreviewDirty = false;
  },
  setTestContactId(state, contactId) {
    state.testContactId = contactId;
  },
  setTestVersionSource(state, versionSource) {
    state.testVersionSource = versionSource;
  },
});

module.add('actions', {
  initNewDraft({ commit }) {
    commit('setDraft', null);
    commit('resetReviewState');
  },

  async fetchAll({ dispatch }) {
    return dispatch('load');
  },

  async fetchOne({ dispatch }, id) {
    return dispatch('loadOne', { path: id });
  },

  async create({ dispatch }, payload) {
    return dispatch('createOne', normalizePayload(payload));
  },

  async update({ dispatch, getters }, { id, payload }) {
    const current = getters.byId(id) || {};
    const normalized = normalizePayload(payload);
    const model = { ...current, ...normalized, id };
    delete model.manifest;
    delete model.published_version_count;
    delete model.published_version_id;
    await dispatch('save', { model });
    return getters.byId(id) || null;
  },

  async fetchAvailableTools({ commit }) {
    try {
      const [catalog, schemaData] = await Promise.all([
        api().get('agent/tools'),
        api().get('agent/tools/schema'),
      ]);
      const tools = normalizeAvailableTools(catalog, schemaData?.schema || {});
      commit('setAvailableTools', tools);
      return tools;
    } catch {
      commit('setAvailableTools', []);
      return [];
    }
  },

  async fetchAgentContacts(_, { agentId, status = 'active' }) {
    const result = await api().get(`agent/custom/contacts/${agentId}`, {
      params: { status },
    });
    return toArray(result);
  },

  async fetchAgentLogs(
    _,
    {
      agentId = null,
      instanceId = null,
      contactId = null,
      type = null,
      page = 1,
      perPage = 50,
    }
  ) {
    if (!instanceId && !agentId) {
      return {
        page,
        per_page: perPage,
        count: 0,
        has_more: false,
        results: [],
      };
    }

    const params = { page, per_page: perPage };
    if (instanceId) {
      params.instance_id = instanceId;
    } else {
      params.agent_id = agentId;
    }
    if (contactId) params.contact_id = contactId;
    if (type) params.type = type;

    const result = await api({ timeout: CONSOLE_API_TIMEOUT_MS }).get(
      'agent/logs',
      { params }
    );

    return result && Array.isArray(result.results)
      ? result
      : { page, per_page: perPage, count: 0, has_more: false, results: [] };
  },

  async consoleEvaluate(
    _,
    { customAgentId, contactId, versionSource = 'head' }
  ) {
    // skipErrorModal: AgentTestTab already renders its own inline error.
    return api({
      timeout: CONSOLE_API_TIMEOUT_MS,
      skipErrorModal: true,
    }).post('agent/console/evaluate', {
      custom_agent_id: customAgentId,
      contact_id: contactId,
      version_source: versionSource || 'head',
    });
  },

  async consoleInbound(
    _,
    { customAgentId, contactId, body, versionSource = 'head' }
  ) {
    return api({
      timeout: CONSOLE_API_TIMEOUT_MS,
      skipErrorModal: true,
    }).post('agent/console/inbound', {
      custom_agent_id: customAgentId,
      contact_id: contactId,
      body,
      version_source: versionSource || 'head',
    });
  },

  async consoleReset(_, { customAgentId, contactId }) {
    return api({ timeout: CONSOLE_API_TIMEOUT_MS }).post(
      'agent/console/reset',
      {
        custom_agent_id: customAgentId,
        contact_id: contactId,
      }
    );
  },

  async consoleTime(_, { customAgentId, contactId, seconds }) {
    return api().post('agent/console/time', {
      custom_agent_id: customAgentId,
      contact_id: contactId,
      seconds,
    });
  },

  async fetchConsoleMessages(_, { customAgentId, contactId }) {
    return api({ timeout: CONSOLE_API_TIMEOUT_MS }).get(
      'agent/console/messages',
      {
        params: { custom_agent_id: customAgentId, contact_id: contactId },
      }
    );
  },

  async enrollContact(_, { agentId, contactId, ifTerminal = 'resume' }) {
    return api({ skipErrorModal: true }).post('agent/custom/enroll', {
      custom_agent_id: agentId,
      contact_id: contactId,
      if_terminal: ifTerminal,
    });
  },

  async unenrollContact(_, { agentId, contactId }) {
    return api().post('agent/custom/unenroll', {
      custom_agent_id: agentId,
      contact_id: contactId,
    });
  },

  async resetContact(_, { agentId, contactId }) {
    return api().post('agent/custom/reset', {
      custom_agent_id: agentId,
      contact_id: contactId,
    });
  },

  async fetchContactInstances(_, contactId) {
    const result = await api().get(
      `agent/custom/contact-instances/${contactId}`
    );
    return toArray(result);
  },

  async remove({ dispatch, getters }, id) {
    const record = getters.byId(id);
    if (!record) return;
    await dispatch('deleteOne', record);
  },

  async fetchVersions({ commit, dispatch }, id) {
    commit('resetVersionsLoaded');
    const token = ++versionsFetchToken;
    const isStale = () => token !== versionsFetchToken;

    let versions = [];
    try {
      versions = toArray(await api().get(`agent/custom/${id}/versions`));
    } catch {
      if (!isStale()) {
        commit('setVersions', []);
        commit('setPublishedVersionSnapshot', null);
      }
      return [];
    }

    if (isStale()) return [];
    commit('setVersions', versions);

    const active = versions.find((v) => v.is_active);
    if (!active) {
      commit('setPublishedVersionSnapshot', null);
      return versions;
    }

    try {
      const snapshot = await dispatch('fetchVersion', {
        id,
        versionId: active.version_id,
      });
      if (!isStale()) commit('setPublishedVersionSnapshot', snapshot);
    } catch {
      if (!isStale()) commit('setPublishedVersionSnapshot', null);
    }

    return versions;
  },

  async fetchVersion(_, { id, versionId }) {
    return api().get(`agent/custom/${id}/versions/${versionId}`);
  },

  async compilePreview({ commit }, id) {
    commit('setCompiling', true);
    try {
      const result = await api().post(`agent/custom/${id}/compile-preview`, {});
      commit('setCompileResult', result);
      return result;
    } finally {
      commit('setCompiling', false);
    }
  },

  async publish({ commit }, { id, manifest, description }) {
    commit('setPublishing', true);
    try {
      const result = await api().post(`agent/custom/${id}/publish`, {
        manifest,
        description,
      });
      commit('setPublishBaseline', null);
      commit('setReviewPreviewDirty', false);
      return result;
    } finally {
      commit('setPublishing', false);
    }
  },

  async activateVersion(
    { dispatch, commit, state },
    { id, versionId, currentDraft = null }
  ) {
    const baseline = currentDraft || state.draft;
    await api().post(`agent/custom/${id}/versions/${versionId}/activate`, {});
    const versions = await dispatch('fetchVersions', id);
    commit('setPublishBaseline', baseline);
    return versions;
  },
});

export default module.create();
